import { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { InsuranceApi } from 'shared/api/insurance'
import EmptyCountryImage from 'shared/images/empty_bag.webp'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { EmptyPage } from 'shared/ui/empty-page'
import { Loader } from 'shared/ui/loader'
import { SearchInput } from 'shared/ui/search-input'

import { useInsuranceDraft } from '../../model/insurance-draft'
import { InsuranceCitizenship } from '../../model/types'
import { InsuranceLayout } from '../../ui/insurance-layout'

import { applyCitizenship } from './lib/apply-citizenship'
import { filterCitizenships } from './lib/filter-citizenships'
import { getInsurancePersonTarget } from './lib/person-target'
import s from './styles.module.scss'

interface CitizenshipInsurancePageProps {
  citizenships: InsuranceCitizenship[] | null
  onCitizenshipsLoaded: (citizenships: InsuranceCitizenship[]) => void
}

const CitizenshipInsurancePage = ({
  citizenships,
  onCitizenshipsLoaded,
}: CitizenshipInsurancePageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { draft, setPolicyholderData } = useInsuranceDraft()
  const target = getInsurancePersonTarget(searchParams)
  const currentCitizenship =
    target?.person === 'policyholder'
      ? draft.policyholder?.citizenship
      : target?.person === 'participant'
        ? draft.participants[target.participantIndex]?.citizenship
        : null
  const [selected, setSelected] = useState(currentCitizenship || null)
  const [isLoading, setIsLoading] = useState(citizenships === null)
  const [isError, setIsError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const [search, setSearch] = useState('')
  const filteredCitizenships = useMemo(
    () => filterCitizenships(citizenships || [], search),
    [citizenships, search],
  )
  const isValidTarget =
    target?.person === 'policyholder'
      ? Boolean(draft.policyholder)
      : target?.person === 'participant'
        ? Boolean(
            draft.policyholder && draft.participants[target.participantIndex],
          )
        : false

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (!isValidTarget) {
      navigate(RoutingPaths.insurance.policyholder.path(), { replace: true })
    }
  }, [isValidTarget, navigate])

  useEffect(() => {
    if (!isValidTarget) return
    if (citizenships !== null) {
      setIsLoading(false)
      setIsError(false)
      return
    }

    const abortController = new AbortController()

    setIsLoading(true)
    setIsError(false)

    InsuranceApi.getCitizenships(abortController.signal)
      .then(({ data }) => onCitizenshipsLoaded(data.data))
      .catch(() => {
        if (!abortController.signal.aborted) setIsError(true)
      })
      .finally(() => {
        if (!abortController.signal.aborted) setIsLoading(false)
      })

    return () => abortController.abort()
  }, [citizenships, isValidTarget, onCitizenshipsLoaded, retryKey])

  if (!target || !isValidTarget) return null

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) =>
    setSearch(event.currentTarget.value)

  const returnToPolicyholder = () => {
    const state =
      target.person === 'participant'
        ? {
            person: target.person,
            participantIndex: target.participantIndex,
          }
        : { person: target.person }

    navigate(RoutingPaths.insurance.policyholder.path(), {
      replace: true,
      state,
    })
  }

  const onContinue = () => {
    if (!selected || !draft.policyholder) return

    const data = applyCitizenship(
      target,
      selected,
      draft.policyholder,
      draft.participants,
    )

    setPolicyholderData(data.policyholder, data.participants)
    returnToPolicyholder()
  }

  return (
    <InsuranceLayout
      title={t('insurance.vzr.policyholder.fields.citizenship')}
      onGoBack={returnToPolicyholder}
      footer={
        !isLoading && !isError
          ? {
              disabled: !selected,
              onClick: onContinue,
              children: t('insurance.vzr.actions.continue'),
            }
          : undefined
      }
    >
      <div className={s.content}>
        <SearchInput
          rootClassName={s.search}
          placeholder={t('insurance.vzr.country.search')}
          onChange={handleSearch}
          onReset={() => setSearch('')}
        />

        {isLoading ? (
          <Loader marginTop={100} />
        ) : isError ? (
          <EmptyPage
            title={t('insurance.vzr.errors.load_title')}
            subTitle={t('error_des')}
            buttonText={t('try_again')}
            onClick={() => setRetryKey((current) => current + 1)}
          />
        ) : !filteredCitizenships.length ? (
          <div className={s.empty}>
            <div className={s.emptyImage}>
              <img
                src={EmptyCountryImage}
                alt=""
                aria-hidden="true"
              />
            </div>
            <div className={s.emptyContent}>
              <p className="bold-22">
                {t('insurance.vzr.country.empty_title')}
              </p>
              <span className="medium-14">
                {t('insurance.vzr.country.empty_description')}
              </span>
            </div>
          </div>
        ) : (
          <div className={s.list}>
            {filteredCitizenships.map((citizenship) => (
              <button
                key={citizenship.id}
                type="button"
                className={s.option}
                aria-pressed={selected?.id === citizenship.id}
                onClick={() => setSelected(citizenship)}
              >
                <span className="medium-16">{citizenship.name}</span>
                {selected?.id === citizenship.id ? (
                  <AppIcon
                    id={AllIcons.CHECK_MARK}
                    className={s.check}
                    width={24}
                    height={24}
                  />
                ) : null}
              </button>
            ))}
          </div>
        )}
      </div>
    </InsuranceLayout>
  )
}

export default CitizenshipInsurancePage
